import { Temporal } from 'temporal-polyfill';
import type { ClassSeries, Location, Occurrence, OccurrenceOverride } from '../domain/types';
import { expandSchedule } from '../domain/schedule';
import { useAsync, useRepository } from './repositoryContext';
import type { AsyncState } from './repositoryContext';

export interface ScheduleView {
  readonly occurrences: Occurrence[];
  readonly locations: Location[];
  readonly series: ClassSeries[];
  readonly overrides: OccurrenceOverride[];
  readonly unresolvedSeriesIds: string[];
}

/**
 * Loads locations, series, and overrides, then expands the schedule for a window.
 *
 * Expansion is recomputed on every load rather than cached: for one instructor's
 * month view this is a handful of milliseconds, and a cache would need
 * invalidation on every series, override, and location edit.
 */
export function useSchedule(
  rangeStart: Temporal.PlainDate,
  rangeEnd: Temporal.PlainDate,
): AsyncState<ScheduleView> {
  const repo = useRepository();
  const startKey = rangeStart.toString();
  const endKey = rangeEnd.toString();

  return useAsync<ScheduleView>(async () => {
    const [locations, series, overrides] = await Promise.all([
      repo.listLocations(),
      repo.listSeries(),
      // Overrides are few (one per deviation), so loading all of them is cheaper
      // than reasoning about how far a reschedule can reach outside the window.
      repo.listOverrides(),
    ]);
    const { occurrences, unresolvedSeriesIds } = expandSchedule({
      series,
      locations,
      overrides,
      rangeStart: Temporal.PlainDate.from(startKey),
      rangeEnd: Temporal.PlainDate.from(endKey),
    });
    return { occurrences, locations, series, overrides, unresolvedSeriesIds };
  }, [startKey, endKey]);
}
